import { useCallback, useEffect, useRef, useState } from "react";
import {
  pipelineBatchUpdatesService,
  TESTIMONIAL_STATUS_MAX_ROWS_PER_CALL,
  type BatchUpdateTestimonialStatusResponse,
  type TestimonialStatusRow,
} from "../../../../api/pipelineBatchUpdates.service.js";
import { clampBatchSize, preventUnload } from "./batchRunHelpers.js";
import { describeApiError } from "./describeApiError.js";

export type TestimonialStatusBatchTotals = {
  received: number;
  notFound: number;
  unchanged: number;
  withdrawnSkipped: number;
  notSuspendable: number;
  updatedTestimonials: number;
  testimonialOnly: number;
  updatedProcessingItems: number;
  updatedApprovalRequests: number;
  notFoundIds: string[];
  withdrawnSkippedIds: string[];
  notSuspendableIds: string[];
};

export type TestimonialStatusBatchProgress = {
  batch: number;
  totalBatches: number;
  processedRows: number;
  totalRows: number;
};

export type TestimonialStatusBatchOptions = {
  batchSize: number;
  dryRun: boolean;
};

export const EMPTY_TOTALS: TestimonialStatusBatchTotals = {
  received: 0,
  notFound: 0,
  unchanged: 0,
  withdrawnSkipped: 0,
  notSuspendable: 0,
  updatedTestimonials: 0,
  testimonialOnly: 0,
  updatedProcessingItems: 0,
  updatedApprovalRequests: 0,
  notFoundIds: [],
  withdrawnSkippedIds: [],
  notSuspendableIds: [],
};

const EMPTY_PROGRESS: TestimonialStatusBatchProgress = {
  batch: 0,
  totalBatches: 0,
  processedRows: 0,
  totalRows: 0,
};

function accumulate(
  totals: TestimonialStatusBatchTotals,
  result: BatchUpdateTestimonialStatusResponse,
): TestimonialStatusBatchTotals {
  return {
    received: totals.received + result.received,
    notFound: totals.notFound + result.notFound,
    unchanged: totals.unchanged + result.unchanged,
    withdrawnSkipped: totals.withdrawnSkipped + result.withdrawnSkipped,
    notSuspendable: totals.notSuspendable + result.notSuspendable,
    updatedTestimonials: totals.updatedTestimonials + result.updatedTestimonials,
    testimonialOnly: totals.testimonialOnly + result.testimonialOnly,
    updatedProcessingItems: totals.updatedProcessingItems + result.updatedProcessingItems,
    updatedApprovalRequests: totals.updatedApprovalRequests + result.updatedApprovalRequests,
    notFoundIds: [...totals.notFoundIds, ...result.notFoundIds],
    withdrawnSkippedIds: [...totals.withdrawnSkippedIds, ...result.withdrawnSkippedIds],
    notSuspendableIds: [...totals.notSuspendableIds, ...result.notSuspendableIds],
  };
}

function batchFailureMessage(
  err: unknown,
  batch: number,
  totalBatches: number,
  from: number,
  to: number,
  dryRun: boolean,
): string {
  const detail = describeApiError(err, "Batch update failed");
  const consequence = dryRun
    ? "Nothing was written (dry run)."
    : "This batch may be partially applied; rows in earlier batches are written. Re-running the same file is safe: rows already in the target status are counted as unchanged.";
  return `Batch ${batch} / ${totalBatches} (rows ${from}–${to}) failed: ${detail}. ${consequence}`;
}

export function useTestimonialStatusBatch() {
  const [running, setRunning] = useState(false);
  const [stopped, setStopped] = useState(false);
  const [dryRun, setDryRun] = useState(true);
  const [operationAt, setOperationAt] = useState<string | null>(null);
  const [progress, setProgress] = useState<TestimonialStatusBatchProgress>(EMPTY_PROGRESS);
  const [totals, setTotals] = useState<TestimonialStatusBatchTotals>(EMPTY_TOTALS);
  const [error, setError] = useState<string | null>(null);
  const stopRef = useRef(false);

  useEffect(() => {
    if (!running) return;
    window.addEventListener("beforeunload", preventUnload);
    return () => window.removeEventListener("beforeunload", preventUnload);
  }, [running]);

  useEffect(() => {
    return () => {
      stopRef.current = true;
    };
  }, []);

  const stop = useCallback(() => {
    stopRef.current = true;
  }, []);

  const reset = useCallback(() => {
    setStopped(false);
    setDryRun(true);
    setOperationAt(null);
    setProgress(EMPTY_PROGRESS);
    setTotals(EMPTY_TOTALS);
    setError(null);
  }, []);

  const start = useCallback(
    async (rows: TestimonialStatusRow[], options: TestimonialStatusBatchOptions) => {
      const batchSize = clampBatchSize(options.batchSize, TESTIMONIAL_STATUS_MAX_ROWS_PER_CALL);
      const totalRows = rows.length;
      const totalBatches = Math.ceil(totalRows / batchSize);
      const at = new Date().toISOString();

      stopRef.current = false;
      setRunning(true);
      setStopped(false);
      setDryRun(options.dryRun);
      setOperationAt(at);
      setError(null);
      setTotals(EMPTY_TOTALS);
      setProgress({ batch: 0, totalBatches, processedRows: 0, totalRows });

      let accumulated = EMPTY_TOTALS;

      try {
        for (let index = 0; index < totalBatches; index++) {
          if (stopRef.current) {
            setStopped(true);
            break;
          }
          const from = index * batchSize;
          const slice = rows.slice(from, from + batchSize);
          const batch = index + 1;
          setProgress({ batch, totalBatches, processedRows: from, totalRows });

          try {
            const result = await pipelineBatchUpdatesService.batchUpdateTestimonialStatus({
              operationAt: at,
              dryRun: options.dryRun,
              rows: slice,
            });
            accumulated = accumulate(accumulated, result);
            setTotals(accumulated);
            setProgress({ batch, totalBatches, processedRows: from + slice.length, totalRows });
          } catch (err) {
            setError(
              batchFailureMessage(
                err,
                batch,
                totalBatches,
                from + 1,
                from + slice.length,
                options.dryRun,
              ),
            );
            break;
          }
        }
      } finally {
        setRunning(false);
      }
    },
    [],
  );

  return { running, stopped, dryRun, progress, totals, error, operationAt, start, stop, reset };
}
